import { errorIdentityOf } from "@pandas/logger";
import { Component, type ErrorInfo, type ReactNode } from "react";
import logger from "@/lib/logging/logger";
import { staleChunkRecovery } from "@/lib/staleChunkRecovery";
import { ErrorFallbackContent } from "./ErrorFallbackContent";

type Props = {
	children: ReactNode;
	fallback?: ReactNode;
};

type State = {
	hasError: boolean;
};

/**
 * Last line of defence for a render that throws.
 *
 * Without one, a render-time failure unmounts the whole tree and leaves a blank page with
 * nothing in the logs, which is how an unreadable price surfaced to users.
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		// A lazy chunk outside the router lands here, not on the route fallback, so it recovers here too.
		if (staleChunkRecovery.maybeRecoverChunkCrash(error)) {
			return;
		}
		// A nested Error serialises to {} in Axiom, so the row carries its identity instead.
		logger.error("INFO", "Unhandled render error", { ...errorIdentityOf(error), componentStack: info.componentStack });
	}

	render() {
		if (!this.state.hasError) return this.props.children;

		return this.props.fallback ?? <ErrorFallbackContent />;
	}
}
